/**
 * Paging maths for the bulk-import preview table.
 *
 * Pulled out of `BulkImport.tsx` so the part that can silently corrupt an import
 * is testable without mounting a 6,000-line modal: the ABSOLUTE index each row
 * carries. Every handler on a preview row addresses its item by position in
 * `items` — the include toggle, the expand toggle, the ref-rewrite opt-out, the
 * tier selector, the exclusion form and both diff maps — so a page that
 * renumbers its rows from zero applies page 2's edits to page 1's references,
 * with nothing on screen to say so.
 */

export interface PreviewPageRow<T> {
  item: T;
  /** Position in the FULL item list, not in the page. */
  index: number;
}

export interface PreviewPageResult<T> {
  rows: Array<PreviewPageRow<T>>;
  /** The page actually rendered — `requestedPage` clamped into range. */
  currentPage: number;
  totalPages: number;
  /** Index of the page's first row in the full list. */
  start: number;
}

/**
 * Slice one page out of `items`, preserving each row's absolute index.
 *
 * `requestedPage` is clamped rather than corrected through state, so a list that
 * shrank under the admin (an exclusion rule applied from the table) shows its
 * new last page instead of an empty one — no setState-in-render, no extra pass.
 * An empty list still reports one page, so the pager has something coherent to
 * render.
 */
export function pagePreviewItems<T>(
  items: T[],
  requestedPage: number,
  pageSize: number,
): PreviewPageResult<T> {
  const size = Math.max(1, Math.floor(pageSize));
  const totalPages = Math.max(1, Math.ceil(items.length / size));
  const currentPage = Math.min(Math.max(1, Math.floor(requestedPage) || 1), totalPages);
  const start = (currentPage - 1) * size;
  const rows = items
    .slice(start, start + size)
    .map((item, offset) => ({ item, index: start + offset }));
  return { rows, currentPage, totalPages, start };
}
